import { readableForeground } from '../utils/colorContrast';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { appSettingsApi } from '../utils/api';
import { useAuthStore } from './authStore';
import { resolveImageUrl } from '../utils/url';
import { primaerLeiter, dunkelModusAktiv } from '../utils/markenfarben';
import { findThemePreset, type ThemePresetId } from '../theme/themePresets';
import { resolveThemeScope } from '../theme/themeScope';

/**
 * Legt die Abstufungen der Markenfarbe als CSS-Variablen auf dem Wurzelelement ab.
 *
 * Die Rechnung selbst steht in utils/markenfarben.ts — dort ohne DOM und damit pruefbar.
 * Hier bleibt nur das Anwenden.
 *
 * WICHTIG: Diese Werte landen INLINE auf <html> und schlagen damit jede Regel aus einem
 * Stylesheet. Der .dark-Block in index.css, der dasselbe per color-mix versuchte, konnte
 * deshalb nie greifen. Wer die Leiter aendern will, aendert sie hier.
 */
export function applyPrimaryShades(root: { style: { setProperty(k: string, v: string): void } }, hex: string, dunkel = false): void {
  for (const [name, wert] of Object.entries(primaerLeiter(hex, dunkel))) {
    root.style.setProperty(name, wert);
  }
}

/** Modusentscheidung fuer den Store — die Eingaben kommen aus Browser-Umgebung. */
export function istDunkelModusAktiv(settings: {
  theme_mode?: 'light' | 'dark' | 'system';
  allow_dark_mode?: boolean;
}): boolean {
  return dunkelModusAktiv(
    settings.theme_mode,
    settings.allow_dark_mode,
    typeof localStorage !== 'undefined' ? localStorage.getItem('darkMode') : null,
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches
  );
}

export interface AppSettings {
  // Branding
  app_name: string;
  logo_url: string | null;
  favicon_url: string | null;
  logo_linkback_url: string | null;
  portal_show_footer_branding: boolean;
  custom_copyright_text: string;
  portal_welcome_title: string;
  portal_welcome_subtitle: string;
  
  // Theme
  theme_mode: 'light' | 'dark' | 'system';
  theme_preset: ThemePresetId;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  success_color: string;
  warning_color: string;
  error_color: string;
  high_contrast: boolean;
  /** full | wide | boxed — Seitenbreite */
  content_layout: 'full' | 'wide' | 'boxed';
  /** Micro-Interaktionen für Buttons/Chips */
  button_interaction_level: 'subtle' | 'balanced' | 'playful';
  allow_dark_mode: boolean;
  custom_css_override: string;
  
  // Typography
  font_family: string;
  base_font_size: '14' | '15' | '16';
  line_height: 'normal' | 'compact';
  google_font_url: string;
  google_font_name: string;
  font_weight_default: 'light' | 'normal' | 'medium';
  
  // UI Behavior
  density: 'compact' | 'comfortable';
  table_row_height: 'small' | 'medium' | 'large';
  sidebar_collapsible: boolean;
  sidebar_default_open: boolean;
  queues_collapsed_default: boolean;
  confirm_critical_actions: boolean;
  default_page_size: number;
  sound_notifications_enabled: boolean;
  sound_notification_url: string;
  
  // Animations
  animations_enabled: 'on' | 'reduced' | 'off';
  page_transitions: boolean;
  dialog_animations: boolean;
  hover_effects: boolean;
  
  // Session & Security
  session_timeout_minutes: number;
  inactivity_logout_enabled: boolean;
  logout_warning_minutes: number;
  allow_multiple_sessions: boolean;
  password_min_length: number;
  password_require_symbols: boolean;
  password_require_numbers: boolean;
  password_require_uppercase: boolean;
  max_login_attempts: number;
  lockout_duration_minutes: number;
  
  // Performance
  auto_refresh_dashboard_seconds: number;
  auto_refresh_ticketlist_seconds: number;
  live_updates_enabled: boolean;
  max_polling_interval_seconds: number;
  min_polling_interval_seconds: number;
  api_cache_enabled: boolean;
  api_cache_ttl_seconds: number;
  system_log_retention_days: number;

  /** System: Agenten dürfen Kunden-Dokumente hochladen/bearbeiten/löschen */
  agent_can_manage_customer_documents?: boolean;
  /** System: Agenten dürfen Kunden-Stammdaten ändern (nicht deaktivieren) */
  agent_can_edit_customer_masterdata?: boolean;

  /** Max. MB pro Ticket-Anhang (global) */
  ticket_attachment_max_mb?: number;
  /** Kommagetrennte Endungen ohne Punkt; leer = System-Standard */
  ticket_attachment_allowed_extensions?: string;
  /** Max. Dateien pro Portal-Ticket-Erstellung */
  ticket_attachment_max_files?: number;

  // Zusammenarbeit & Chat
  internal_chat_enabled?: boolean;
  internal_chat_notifications_enabled?: boolean;
  internal_chat_retention_days?: number;
  admin_default_view?: 'dashboard' | 'settings';
}

const DEFAULT_SETTINGS: AppSettings = {
  app_name: 'NALVRA SERVICE',
  logo_url: null,
  favicon_url: null,
  logo_linkback_url: null,
  portal_show_footer_branding: true,
  custom_copyright_text: '',
  portal_welcome_title: '',
  portal_welcome_subtitle: '',
  theme_mode: 'light',
  theme_preset: 'indigo-harbor',
  primary_color: '#23439B',
  secondary_color: '#475569',
  accent_color: '#3158B7',
  success_color: '#22C55E',
  warning_color: '#F59E0B',
  error_color: '#EF4444',
  high_contrast: false,
  content_layout: 'full',
  button_interaction_level: 'balanced',
  allow_dark_mode: true,
  custom_css_override: '',
  font_family: "'Open Sans', sans-serif",
  base_font_size: '16',
  line_height: 'normal',
  google_font_url: '',
  google_font_name: '',
  font_weight_default: 'normal',
  density: 'comfortable',
  table_row_height: 'medium',
  sidebar_collapsible: true,
  sidebar_default_open: true,
  queues_collapsed_default: false,
  confirm_critical_actions: true,
  default_page_size: 25,
  sound_notifications_enabled: false,
  sound_notification_url: '/sounds/notification-ping.mp3',
  animations_enabled: 'on',
  page_transitions: true,
  dialog_animations: true,
  hover_effects: true,
  session_timeout_minutes: 60,
  inactivity_logout_enabled: true,
  logout_warning_minutes: 2,
  allow_multiple_sessions: true,
  password_min_length: 8,
  password_require_symbols: false,
  password_require_numbers: false,
  password_require_uppercase: false,
  max_login_attempts: 5,
  lockout_duration_minutes: 15,
  auto_refresh_dashboard_seconds: 300,
  auto_refresh_ticketlist_seconds: 60,
  live_updates_enabled: true,
  max_polling_interval_seconds: 30,
  min_polling_interval_seconds: 5,
  api_cache_enabled: false,
  api_cache_ttl_seconds: 60,
  system_log_retention_days: 90,
  agent_can_manage_customer_documents: false,
  agent_can_edit_customer_masterdata: false,
  ticket_attachment_max_mb: 10,
  ticket_attachment_allowed_extensions: '',
  ticket_attachment_max_files: 5,
  internal_chat_enabled: true,
  internal_chat_notifications_enabled: true,
  internal_chat_retention_days: 30,
  admin_default_view: 'dashboard',
};

interface AppSettingsState {
  settings: AppSettings;
  brandingAllowed: boolean;
  vendorFooterText: string;
  portalTheme: ReturnType<typeof resolveThemeScope> | null;
  setPortalTheme: (value: AppSettingsState['portalTheme']) => void;
  themeOverride: { themePreset?: ThemePresetId; themeMode?: 'light' | 'dark' | 'system' } | null;
  isLoading: boolean;
  isInitialized: boolean;
  
  // Actions
  loadSettings: () => Promise<void>;
  updateSettings: (updates: Partial<AppSettings>) => Promise<void>;
  applySettingsToDOM: () => void;
  setThemeOverride: (value: AppSettingsState['themeOverride']) => void;
}

export const useAppSettingsStore = create<AppSettingsState>()(
  persist(
    (set, get) => ({
      settings: DEFAULT_SETTINGS,
      brandingAllowed: false,
      vendorFooterText: '',
      portalTheme: null,
      setPortalTheme: (portalTheme) => {
        set({ portalTheme });
        get().applySettingsToDOM();
      },
      themeOverride: null,
      isLoading: false,
      isInitialized: false,

      setThemeOverride: (themeOverride) => {
        set({ themeOverride });
        get().applySettingsToDOM();
      },

      loadSettings: async () => {
        try {
          set({ isLoading: true });
          const isAuthed = useAuthStore.getState().isAuthenticated;
          let response;
          if (isAuthed) {
            try {
              response = await appSettingsApi.getAll();
            } catch (error: any) {
              // Eine abgelaufene persistierte Sitzung darf das Branding der
              // Anmeldeseite nicht in einen Fehlerzustand ziehen.
              if (error?.response?.status !== 401) throw error;
              response = await appSettingsApi.getPublic();
            }
          } else {
            response = await appSettingsApi.getPublic();
          }
          const serverSettings = response.data?.settings || {};
          
          const mergedSettings = { ...DEFAULT_SETTINGS, ...serverSettings };
          set({
            settings: mergedSettings,
            brandingAllowed: !!response.data?.branding?.branding_allowed,
            vendorFooterText: String(response.data?.branding?.footer_text || ''),
            isInitialized: true,
          });
        } catch (error) {
          console.error('Failed to load app settings:', error);
          set({ isInitialized: true });
        } finally {
          set({ isLoading: false });
          get().applySettingsToDOM();
        }
      },

      updateSettings: async (updates) => {
        try {
          set({ isLoading: true });
          await appSettingsApi.update(updates);
          
          const currentSettings = get().settings;
          const newSettings = { ...currentSettings, ...updates };
          set({ settings: newSettings });
          
          try {
            get().applySettingsToDOM();
          } catch (domError) {
            console.warn('applySettingsToDOM error (non-fatal):', domError);
          }
        } catch (error) {
          console.error('Failed to update app settings:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      applySettingsToDOM: () => {
        const { settings, themeOverride } = get();
        const effectiveSettings = themeOverride
          ? {
              ...settings,
              theme_preset: themeOverride.themePreset || settings.theme_preset,
              theme_mode: themeOverride.themeMode || settings.theme_mode,
            }
          : settings;
        const personalPreset = findThemePreset(themeOverride?.themePreset);
        const presentation = personalPreset ? {
          ...settings, base_font_size: personalPreset.fontSize, density: personalPreset.density,
          table_row_height: personalPreset.rowHeight, content_layout: personalPreset.layout,
          line_height: personalPreset.density === 'compact' ? 'compact' : 'normal',
        } : settings;
        const root = document.documentElement;
        const body = document.body;

        // Der Modus wird ZUERST bestimmt, weil die Farbabstufungen davon abhängen —
        // im dunklen Modus mischen die hellen Stufen gegen den dunklen Grund statt gegen
        // Weiß. Früher stand diese Entscheidung weiter unten, und die Leiter wurde
        // grundsätzlich hell berechnet.
        const dunkelAktiv = istDunkelModusAktiv(effectiveSettings);

        // Das Preset steuert die semantischen Oberflächen in index.css. Die
        // individuellen Markenfarben bleiben parallel erhalten und bilden den
        // kompatiblen Rückfall für ältere Komponenten.
        const presetId = effectiveSettings.theme_preset || 'custom';
        const preset = findThemePreset(presetId);
        const palette = preset ? (dunkelAktiv ? preset.dark : preset.light) : undefined;
        root.setAttribute('data-theme-preset', presetId);
        root.style.setProperty('--theme-background', palette?.background || (dunkelAktiv ? '#0a0a0f' : '#f9fafb'));
        root.style.setProperty('--theme-surface', palette?.surface || (dunkelAktiv ? '#111827' : '#ffffff'));
        root.style.setProperty('--theme-surface-muted', palette?.surfaceMuted || (dunkelAktiv ? '#1f2937' : '#f3f4f6'));
        root.style.setProperty('--theme-border', palette?.border || (dunkelAktiv ? '#374151' : '#e5e7eb'));
        root.style.setProperty('--theme-text', palette?.text || (dunkelAktiv ? '#e5e7eb' : '#111827'));
        root.style.setProperty('--theme-text-muted', palette?.textMuted || (dunkelAktiv ? '#9ca3af' : '#6b7280'));
        root.style.setProperty('--theme-primary-foreground', palette?.primaryForeground || '#ffffff');
        root.style.setProperty('--theme-radius', preset?.radius || '0.75rem');

        // ── Colors (CSS custom properties) ──────────────────────
        const effectivePrimary = palette?.primary || effectiveSettings.primary_color;
        root.style.setProperty('--color-primary', effectivePrimary);
        root.style.setProperty('--color-primary-foreground', readableForeground(effectivePrimary));
        root.style.setProperty('--theme-primary-foreground', readableForeground(effectivePrimary));
        // Abstufungen aus der Grundfarbe ableiten. Ohne sie blieben --color-primary-50…950
        // auf ihren Rückfallwerten stehen (dem alten Gelb) — eine geänderte Corporate-Farbe
        // hätte fast nirgends gewirkt, weil die Oberfläche die Abstufungen nutzt, nicht die
        // Grundfarbe. Genau das ließ das White-Label-Versprechen in der Vorführung
        // zusammenbrechen.
        applyPrimaryShades(root, effectivePrimary, dunkelAktiv);
        root.style.setProperty('--color-primary-600-foreground', readableForeground(root.style.getPropertyValue('--color-primary-600').trim() || effectivePrimary));
        root.style.setProperty('--color-secondary', palette?.secondary || effectiveSettings.secondary_color);
        root.style.setProperty('--color-accent', palette?.accent || effectiveSettings.accent_color);
        root.style.setProperty('--color-success', settings.success_color);
        root.style.setProperty('--color-warning', settings.warning_color);
        root.style.setProperty('--color-error', palette?.destructive || settings.error_color);
        
        // ── Typography & Google Fonts ───────────────────────────
        if (!themeOverride && settings.google_font_url) {
          let fontLink = document.getElementById('custom-google-font') as HTMLLinkElement;
          if (!fontLink) {
            fontLink = document.createElement('link');
            fontLink.id = 'custom-google-font';
            fontLink.rel = 'stylesheet';
            document.head.appendChild(fontLink);
          }
          fontLink.href = settings.google_font_url;
          root.style.setProperty('--font-family', settings.google_font_name || 'sans-serif');
        } else {
          document.getElementById('custom-google-font')?.remove();
          root.style.setProperty('--font-family', preset?.fontFamily || effectiveSettings.font_family);
        }

        root.style.setProperty('--base-font-size', `${presentation.base_font_size}px`);
        root.style.setProperty('--line-height', presentation.line_height === 'compact' ? '1.4' : '1.6');
        
        const weightMap: Record<string, string> = { light: '300', normal: '400', medium: '500' };
        body.style.fontWeight = weightMap[settings.font_weight_default] || '400';

        // ── Custom CSS Override ─────────────────────────────────
        let styleBlock = document.getElementById('custom-css-override');
        if (settings.custom_css_override) {
          if (!styleBlock) {
            styleBlock = document.createElement('style');
            styleBlock.id = 'custom-css-override';
            document.head.appendChild(styleBlock);
          }
          styleBlock.textContent = settings.custom_css_override;
        } else {
          styleBlock?.remove();
        }

        // ── Apply Dark Mode based on theme_mode and allow_dark_mode ───
        // Dieselbe Entscheidung wie oben — sie steht in istDunkelModusAktiv, damit die
        // Farbabstufungen und die `dark`-Klasse nicht auseinanderlaufen koennen.
        const applyDark = dunkelAktiv;
        if (applyDark) {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
        
        // ── Content width (Seiten-Container .app-page-container) ─
        const layout = presentation.content_layout === 'wide' || presentation.content_layout === 'boxed' ? presentation.content_layout : 'full';
        body.setAttribute('data-content-layout', layout);

        // ── Button micro-interactions (CSS variables) ───────────
        const btnLevel =
          settings.button_interaction_level === 'subtle' || settings.button_interaction_level === 'playful'
            ? settings.button_interaction_level
            : 'balanced';
        body.setAttribute('data-button-interaction', btnLevel);

        // ── Density (data attribute on <body>) ──────────────────
        body.setAttribute('data-density', presentation.density);
        
        // ── Table Row Height (data attribute on <body>) ─────────
        body.setAttribute('data-table-row-height', presentation.table_row_height);
        
        // ── High Contrast ───────────────────────────────────────
        body.classList.toggle('high-contrast', settings.high_contrast);
        
        // ── Animations ──────────────────────────────────────────
        body.classList.remove('no-animations', 'reduced-animations');
        if (settings.animations_enabled === 'off') {
          body.classList.add('no-animations');
        } else if (settings.animations_enabled === 'reduced') {
          body.classList.add('reduced-animations');
        }
        
        // ── Granular animation toggles ──────────────────────────
        body.classList.toggle('no-hover-effects', !settings.hover_effects);
        body.classList.toggle('no-page-transitions', !settings.page_transitions);
        body.classList.toggle('no-dialog-animations', !settings.dialog_animations);
        
        // ── App Name → document title ───────────────────────────
        document.title = settings.app_name;
        
        // ── Favicon ─────────────────────────────────────────────
        if (settings.favicon_url) {
          let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
          if (!favicon) {
            favicon = document.createElement('link');
            favicon.rel = 'icon';
            document.head.appendChild(favicon);
          }
          favicon.href = resolveImageUrl(settings.favicon_url);
        }
        
        // ── Logo URL (store as data attribute for components) ───
        if (settings.logo_url) {
          root.setAttribute('data-logo-url', resolveImageUrl(settings.logo_url));
        } else {
          root.removeAttribute('data-logo-url');
        }

        // The mounted portal owns document-level dark variants and rem sizing.
        // Keep this transient: late settings loads must not restore the agent theme.
        const portalTheme = get().portalTheme;
        if (portalTheme) {
          root.classList.toggle('dark', portalTheme.dark);
          root.setAttribute('data-theme-preset', portalTheme.presetId);
          Object.entries(portalTheme.style).forEach(([name, value]) => {
            root.style.setProperty(name, String(value));
          });
        }
      },
    }),
    {
      name: 'app-settings-storage',
      partialize: (state) => ({ settings: state.settings }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.applySettingsToDOM();
        }
      }
    }
  )
);
